import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Field } from '@shared/ui/primitives/Field';
import { Banner } from '@shared/ui/feedback/Banner';
import { CountryCodePicker, findCountry } from '@shared/ui/primitives/CountryCodePicker';
import { SegmentedControl } from '@shared/ui/lists/SegmentedControl';
import { ActionButton, CheckboxOption } from '@shared/ui/forms/FormFields';
import { Card } from '@shared/ui/containers/Card';
import { focusNextOnEnter, applyEnterKeyHint } from '@shared/lib/formNav';
import { env } from '@config/env';
import { pageCopy } from '@shared/lib/pageCopy';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { useModuleStore } from '@shared/stores/moduleStore';
import { useAuthStore } from '@shared/stores/authStore';
import { useBrandImagery } from '@shared/hooks/useBrandImagery';
import { buildE164 } from '@shared/lib/phone';
import { toApiError } from '@shared/api/errors';
import { resetToHubContext } from '@shared/api/domainApi';
import { getEffectiveRootTenantId } from '@shared/lib/ssoRootScope';
import { login, requestLoginOtp, verifyLoginOtp } from './api';

type Mode = 'password' | 'otp';
type Method = 'email' | 'mobile';

/**
 * Sign-in screen. Honours the tenant's `authConfig`: which identifier
 * (email / mobile / both) is accepted, and whether sign-in is by password,
 * one-time code, or either. When mobile is allowed the number is entered
 * with a country-code picker and sent as an E.164 identifier.
 */
export function Login(): JSX.Element {
  const navigate = useNavigate();
  const branding = useBrandingStore((s) => s.branding);
  const authConfig = useModuleStore((s) => s.config?.authConfig);
  const rememberedByDefault = useAuthStore((s) => s.rememberSession);
  const setRememberSession = useAuthStore((s) => s.setRememberSession);
  const { authBackground } = useBrandImagery();
  const name = branding?.name ?? 'IP4Estates';

  const allowedIdentifiers = authConfig?.allowed_identifiers ?? 'both';
  const allowedModes = authConfig?.allowed_login_modes ?? 'password_or_otp';
  const canPassword = allowedModes !== 'otp';
  const canOtp = allowedModes !== 'password';
  const canEmail = allowedIdentifiers !== 'mobile';
  const canMobile = allowedIdentifiers !== 'email';

  const [mode, setMode] = useState<Mode>(canPassword ? 'password' : 'otp');
  const [method, setMethod] = useState<Method>(canEmail ? 'email' : 'mobile');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('ZA');
  const [localMobile, setLocalMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(rememberedByDefault);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dialCode = findCountry(countryCode).dialCode;
  const mobileE164 = buildE164(dialCode, localMobile);
  // The identifier sent to the API — an email or a full E.164 mobile number.
  const identifier = method === 'mobile' ? mobileE164 : email.trim();
  const identifierFilled =
    method === 'mobile' ? localMobile.trim().length >= 5 : email.trim().length > 0;

  const onDone = () => navigate('/app/home', { replace: true });

  // Landing on sign-in re-establishes the SSO hub context (branding + domain
  // config) — SSO sign-in goes via the hub, never a picked community.
  useEffect(() => {
    if (getEffectiveRootTenantId(env.rootTenantId)) {
      void resetToHubContext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBack = () => {
    if (getEffectiveRootTenantId(env.rootTenantId)) {
      void resetToHubContext();
    }
    navigate('/welcome');
  };

  const passwordLogin = useMutation({
    mutationFn: () => login({ identifier, password, remember: rememberMe }),
    onSuccess: onDone,
    onError: (e) => setError(toApiError(e).message),
  });
  const sendOtp = useMutation({
    mutationFn: () => requestLoginOtp(identifier),
    onSuccess: () => {
      setOtpSent(true);
      setError(null);
    },
    onError: (e) => setError(toApiError(e).message),
  });
  const verifyOtp = useMutation({
    mutationFn: () => verifyLoginOtp(identifier, otpCode),
    onSuccess: onDone,
    onError: (e) => setError(toApiError(e).message),
  });

  const pending = passwordLogin.isPending || sendOtp.isPending || verifyOtp.isPending;

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setOtpSent(false);
    setOtpCode('');
  };

  const switchMethod = (next: Method) => {
    setMethod(next);
    setError(null);
    setOtpSent(false);
    setOtpCode('');
  };

  const inputClass =
    'h-12 w-full rounded-md border border-hairline-strong bg-elevated px-3.5 text-body text-ink placeholder:text-ink-muted focus:border-primary-600 focus:outline-none disabled:opacity-60';

  const modeSelector =
    canPassword && canOtp ? (
      <SegmentedControl<Mode>
        fullWidth
        options={[
          { value: 'password', label: 'Password' },
          { value: 'otp', label: 'One-time code' },
        ]}
        value={mode}
        onChange={switchMode}
      />
    ) : null;

  const identifierSelector =
    canEmail && canMobile ? (
      <SegmentedControl<Method>
        fullWidth
        options={[
          { value: 'email', label: 'Email' },
          { value: 'mobile', label: 'Mobile' },
        ]}
        value={method}
        onChange={switchMethod}
      />
    ) : null;

  /* The identifier input — an email field, or a country picker + mobile
     field — shared by both the password and one-time-code forms. */
  const identifierField =
    method === 'email' ? (
      <Field
        label="Email"
        type="email"
        inputMode="email"
        autoComplete="username"
        autoCapitalize="off"
        autoCorrect="off"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        disabled={otpSent}
      />
    ) : (
      <div className="flex flex-col gap-1.5">
        <span className="text-body-sm font-medium text-ink">Mobile number</span>
        <div className="flex gap-2">
          <CountryCodePicker value={countryCode} onChange={(c) => setCountryCode(c.code)} />
          <input
            value={localMobile}
            onChange={(e) => setLocalMobile(e.target.value.replace(/\D/g, '').slice(0, 15))}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            placeholder="82 123 4567"
            disabled={otpSent}
            className={inputClass}
          />
        </div>
      </div>
    );

  return (
    <div className="flex h-full flex-col overflow-hidden bg-paper">
      {/* Photo header — fills whatever space the card doesn't need, with a floor */}
      <div className="relative min-h-[120px] flex-1 overflow-hidden bg-primary-900">
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${authBackground}")` }}
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/65" />
        <div className="relative flex items-center px-5 pt-safe-or-6">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="flex h-9 w-9 items-center justify-center rounded-pill bg-white/15 text-white backdrop-blur"
          >
            <ArrowLeft className="h-4.5 w-4.5" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {/* Form card — grows with its content up to a capped height, then the
          card body scrolls internally rather than the whole page. */}
      <div className="relative -mt-7 flex max-h-[80dvh] shrink-0 flex-col overflow-hidden rounded-t-3xl bg-card shadow-sheet">
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-7 pb-safe-or-6">
        {branding?.logoUrl ? (
          <img
            src={branding.logoUrl}
            alt={name}
            className="mb-5 h-9 w-auto max-w-[55%] object-contain"
          />
        ) : null}
        <h1 className="display-serif text-display-lg text-ink">{pageCopy().auth.loginTitle}</h1>
        <p className="mt-1 text-body text-ink-secondary">{pageCopy().auth.loginSubtitle}</p>

        {modeSelector || identifierSelector ? (
          <div className="mt-4 flex flex-col gap-3">
            {modeSelector}
            {identifierSelector}
          </div>
        ) : null}

        {error ? <Banner variant="danger" title={error} className="mt-4 rounded-md" /> : null}

        <form
          className="mt-4 flex flex-col gap-4"
          onKeyDown={focusNextOnEnter}
          onFocus={applyEnterKeyHint}
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            setRememberSession(rememberMe);
            if (mode === 'password') passwordLogin.mutate();
            else if (otpSent) verifyOtp.mutate();
            else sendOtp.mutate();
          }}
        >
          {identifierField}

          {/* ── Password mode ──────────────────────────────────────── */}
          {mode === 'password' ? (
            <>
              <Field
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="Your password"
                trailing={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="text-ink-muted"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4.5 w-4.5" strokeWidth={1.5} />
                    ) : (
                      <Eye className="h-4.5 w-4.5" strokeWidth={1.5} />
                    )}
                  </button>
                }
              />
              <Card noGrain className="overflow-hidden p-0 shadow-none">
                <CheckboxOption
                  label="Remember me"
                  description="Keep you signed in on this device."
                  checked={rememberMe}
                  onChange={setRememberMe}
                  className="rounded-none border-0 bg-transparent px-4 py-3.5"
                />
              </Card>
              <ActionButton
                type="submit"
                loading={passwordLogin.isPending}
                disabled={!identifierFilled || !password}
              >
                Sign in
              </ActionButton>
              <div className="flex justify-center">
                <Link
                  to="/forgot-password"
                  className="text-body-sm font-medium text-primary-700 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
            </>
          ) : (
            /* ── One-time-code mode ───────────────────────────────── */
            <>
              {otpSent ? (
                <Field
                  label="One-time code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="6-digit code"
                  helper={`We sent a code to ${identifier}.`}
                />
              ) : null}

              <ActionButton
                type="submit"
                loading={sendOtp.isPending || verifyOtp.isPending}
                disabled={otpSent ? !otpCode.trim() : !identifierFilled}
              >
                {otpSent ? 'Verify & sign in' : 'Send me a code'}
              </ActionButton>

              {otpSent ? (
                <div className="flex justify-between text-body-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setOtpSent(false);
                      setOtpCode('');
                      setError(null);
                    }}
                    className="font-medium text-ink-secondary hover:underline"
                  >
                    Change details
                  </button>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => sendOtp.mutate()}
                    className="font-medium text-primary-700 hover:underline disabled:opacity-50"
                  >
                    Resend code
                  </button>
                </div>
              ) : null}
            </>
          )}
        </form>

        <p className="mt-4 text-center text-body-sm text-ink-secondary">
          Not registered yet?{' '}
          <Link to="/signup" className="font-semibold text-primary-700 hover:underline">
            Create an account
          </Link>
        </p>
        </div>
      </div>
    </div>
  );
}
